import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';
import MovieCard from '@/components/feature/MovieCard';
import { allContent, categoryMap } from '@/mocks/content';
import type { Movie } from '@/mocks/content';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();

  const movie = useMemo(() => allContent.find((m: Movie) => m.id === id), [id]);
  const related = useMemo(() => {
    if (!movie) return [];
    return allContent
      .filter((m: Movie) => m.id !== movie.id && m.category === movie.category)
      .slice(0, 5);
  }, [movie]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查找的影片不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catLabel = categoryMap[movie.category]?.label || '';

  const detailTitle = `${movie.title} - 高清免费在线观看 | 色影视`;
  const detailDesc = `${movie.title}：${movie.description.slice(0, 100)}... 色影视提供${movie.title}超清免费在线观看，不缓冲流畅播放，无需注册付费。`;
  const detailKeywords = `${movie.title},${movie.title}在线观看,${movie.title}高清免费,${movie.subCategory}${catLabel},色影视`;

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    datePublished: movie.year,
    director: { '@type': 'Person', name: movie.director },
    actor: movie.actors.map((a: string) => ({ '@type': 'Person', name: a })),
    countryOfOrigin: { '@type': 'Country', name: movie.region },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.seyingshi.com/' },
      { '@type': 'ListItem', position: 2, name: catLabel, item: `https://www.seyingshi.com/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <PageSEO
        title={detailTitle}
        description={detailDesc}
        keywords={detailKeywords}
        canonicalUrl={`https://www.seyingshi.com/detail/${movie.id}`}
        ogType="video.movie"
        jsonLd={[articleLd, breadcrumbLd]}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb />

          {/* Detail Hero */}
          <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Poster */}
              <div className="flex-shrink-0 w-full md:w-[300px]">
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 海报`}
                    title={`${movie.title} - 高清免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {movie.title}
                </h1>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300" />
                  <span className="text-sm text-foreground-500">{movie.subCategory}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300" />
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300" />
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  {movie.episodes !== '1' && (
                    <>
                      <span className="w-1 h-1 rounded-full bg-foreground-300" />
                      <span className="text-sm text-foreground-500">{movie.episodes}</span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-lg" />
                    <span className="text-lg font-semibold text-foreground-900">{movie.rating}</span>
                  </div>
                  {movie.isHot && (
                    <span className="text-xs font-medium bg-primary-100 text-primary-700 px-2.5 py-1 rounded-md">热播中</span>
                  )}
                  {movie.isNew && (
                    <span className="text-xs font-medium bg-accent-100 text-accent-700 px-2.5 py-1 rounded-md">新上线</span>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {movie.tags.map((tag: string) => (
                    <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-md">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {movie.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <span className="text-xs text-foreground-400">导演</span>
                    <p className="text-sm text-foreground-700 mt-0.5">{movie.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">主演</span>
                    <p className="text-sm text-foreground-700 mt-0.5">{movie.actors.join(' / ')}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">地区</span>
                    <p className="text-sm text-foreground-700 mt-0.5">{movie.region}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">更新日期</span>
                    <p className="text-sm text-foreground-700 mt-0.5">{movie.updateDate}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-fill text-lg" />
                    立即播放
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-primary-500 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                    <i className="ri-heart-line text-lg" />
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-primary-500 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                    <i className="ri-share-forward-line text-lg" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Related */}
          {related.length > 0 && (
            <section className="w-full bg-background-100 py-2">
              <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground-900">相关推荐</h2>
                    <p className="text-sm text-foreground-500 mt-1">你可能也会喜欢这些</p>
                  </div>
                  <Link
                    to={`/category/${movie.category}`}
                    className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
                  >
                    更多{movie.category === 'movie' ? '电影' : movie.category === 'tv' ? '电视剧' : '综艺'}
                    <i className="ri-arrow-right-s-line" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="true">
                  {related.map((m: Movie) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}